import type {
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
} from '@/features/campaigns/types/campaign';
import { getCachedContentPreview } from '@/features/campaigns/utils/contentPreviewCache';

/** Prefer non-blob URLs — blob: previews are stripped from drafts and die on remount. */
export function isDurableMediaUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  return !trimmed.startsWith('blob:');
}

export function pickMediaUrl(
  ...candidates: Array<string | undefined | null>
): string {
  for (const c of candidates) {
    if (c?.startsWith('blob:')) return String(c).trim();
  }
  for (const c of candidates) {
    if (isDurableMediaUrl(c)) return String(c).trim();
  }
  for (const c of candidates) {
    if (c && String(c).trim()) return String(c).trim();
  }
  return '';
}

export function isVideoContent(type?: string | null, url?: string | null): boolean {
  const t = String(type || '').toLowerCase().trim();
  if (t.startsWith('video') || t === 'video') return true;
  if (/\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(String(url || ''))) return true;
  return false;
}

function fromPreviewPayload(data?: PreviewData | null): CreativePreview {
  if (!data) return { url: '', type: '' };

  const mediaIds = (data as { mediaIds?: unknown }).mediaIds;
  if (Array.isArray(mediaIds) && mediaIds.length > 0) {
    const first = mediaIds[0] as Record<string, unknown>;
    const populated = first?.mediaId;
    if (populated && typeof populated === 'object') {
      const media = populated as Record<string, unknown>;
      return {
        url: String(media.url || media.mediaUrl || ''),
        type: String(media.mimetype || media.mediaType || ''),
      };
    }
    return {
      url: String(first?.mediaUrl || first?.url || ''),
      type: String(first?.mediaType || first?.mimetype || ''),
    };
  }

  const details = data.mediaDetails;
  if (Array.isArray(details) && details[0]) {
    const d = details[0] as Record<string, unknown>;
    return {
      url: String(d.url || d.mediaUrl || ''),
      type: String(d.mimetype || d.mediaType || ''),
    };
  }
  if (details && typeof details === 'object') {
    const d = details as Record<string, unknown>;
    return {
      url: String(d.url || d.mediaUrl || ''),
      type: String(d.mimetype || d.mediaType || ''),
    };
  }

  return { url: '', type: '' };
}

/** Resolve a displayable content preview from wizard state + preview payload. */
export function resolveCreativePreview(
  creativePreview: CreativePreview | undefined,
  campaignData?: Pick<
    ManualCampaignForm,
    'mediaUrl' | 'mediaType' | 'mediaIds'
  > | null,
  previewData?: PreviewData | null,
): CreativePreview {
  const mediaId = campaignData?.mediaIds?.[0]?.mediaId;
  const cached = getCachedContentPreview(mediaId);
  if (cached?.blobUrl) {
    return {
      url: cached.blobUrl,
      type:
        cached.type ||
        creativePreview?.type ||
        campaignData?.mediaType ||
        campaignData?.mediaIds?.[0]?.mediaType ||
        '',
    };
  }

  // Live blob URL always works in the current tab
  if (creativePreview?.url?.startsWith('blob:')) {
    return {
      url: creativePreview.url,
      type:
        creativePreview.type ||
        campaignData?.mediaType ||
        campaignData?.mediaIds?.[0]?.mediaType ||
        '',
    };
  }

  const fromApi = fromPreviewPayload(previewData);
  const url = pickMediaUrl(
    creativePreview?.url,
    campaignData?.mediaUrl,
    campaignData?.mediaIds?.[0]?.mediaUrl,
    fromApi.url,
  );
  if (!url) return { url: '', type: '' };

  const type =
    creativePreview?.type ||
    campaignData?.mediaType ||
    campaignData?.mediaIds?.[0]?.mediaType ||
    fromApi.type ||
    '';

  return { url, type };
}
